import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowLeft, Play, Pause, SkipForward, SkipBack, Loader2, ListMusic, Volume2, VolumeX, Share2 } from 'lucide-react';
import { API } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { toast } from '../hooks/use-toast';

const AlbumView = () => {
  const { id } = useParams();
  const { lang } = useLang();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);

  const colorThemes = {
    cyan: { from: 'from-cyan-500', to: 'to-fuchsia-500', ring: 'border-cyan-400/40', ringBg: 'bg-cyan-500/20', accent: 'text-cyan-300', chip: 'text-cyan-400', btn: 'bg-cyan-500 hover:bg-cyan-600' },
    pink: { from: 'from-pink-500', to: 'to-rose-500', ring: 'border-pink-400/40', ringBg: 'bg-pink-500/20', accent: 'text-pink-300', chip: 'text-pink-400', btn: 'bg-pink-500 hover:bg-pink-600' },
    amber: { from: 'from-amber-500', to: 'to-orange-500', ring: 'border-amber-400/40', ringBg: 'bg-amber-500/20', accent: 'text-amber-300', chip: 'text-amber-400', btn: 'bg-amber-500 hover:bg-amber-600' },
    emerald: { from: 'from-emerald-500', to: 'to-teal-500', ring: 'border-emerald-400/40', ringBg: 'bg-emerald-500/20', accent: 'text-emerald-300', chip: 'text-emerald-400', btn: 'bg-emerald-500 hover:bg-emerald-600' },
    violet: { from: 'from-violet-500', to: 'to-indigo-500', ring: 'border-violet-400/40', ringBg: 'bg-violet-500/20', accent: 'text-violet-300', chip: 'text-violet-400', btn: 'bg-violet-500 hover:bg-violet-600' },
    slate: { from: 'from-slate-600', to: 'to-neutral-700', ring: 'border-slate-400/40', ringBg: 'bg-slate-500/20', accent: 'text-slate-300', chip: 'text-slate-400', btn: 'bg-slate-600 hover:bg-slate-700' }
  };
  const theme = colorThemes[album?.color] || colorThemes.cyan;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/albums/${id}`);
        setAlbum(res.data);
      } catch (e) { setError(e.response?.data?.detail || 'Album not found'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const videos = album?.videos || [];
  const current = videos[idx];

  useEffect(() => {
    if (videoRef.current && current) {
      videoRef.current.load();
      if (playing) videoRef.current.play().catch(() => {});
    }
  }, [idx, current, playing]);

  const next = () => setIdx(i => Math.min(i + 1, videos.length - 1));
  const prev = () => setIdx(i => Math.max(i - 1, 0));
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) v.pause(); else v.play().catch(() => {});
    setPlaying(!playing);
  };

  const shareAlbum = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast({ title: lang === 'sv' ? 'Länk kopierad!' : 'تم النسخ!' }); } catch (_) {}
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  if (error || !album) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-950 text-white gap-4">
      <p className="text-neutral-400">{lang === 'sv' ? 'Albumet kunde inte hittas' : 'الألبوم غير موجود'}</p>
      <a href="/" className="text-cyan-400 font-semibold text-sm">{lang === 'sv' ? 'Startsidan' : 'الرئيسية'}</a>
    </div>
  );

  if (videos.length === 0) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-950 text-white gap-3">
      <ListMusic className="w-10 h-10 text-neutral-500" />
      <p className="text-neutral-400">{lang === 'sv' ? 'Detta album är tomt' : 'الألبوم فارغ'}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <div className="flex items-center justify-between mb-6">
          <a href="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-[13px] font-semibold"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {lang === 'sv' ? 'Tillbaka' : 'رجوع'}</a>
          <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div><span className="font-black text-white text-[15px]">CapCute</span></div>
        </div>

        <div className="mb-6">
          <div className={`inline-flex items-center gap-2 bg-white/5 rounded-full px-3 py-1 mb-3`}>
            <ListMusic className={`w-3.5 h-3.5 ${theme.chip}`} />
            <span className={`text-[11px] font-bold uppercase tracking-wider ${theme.accent}`}>{lang === 'sv' ? 'Album' : 'ألبوم'}</span>
          </div>
          <h1 className={`text-3xl md:text-4xl font-black tracking-tight bg-gradient-to-r ${theme.from} ${theme.to} bg-clip-text text-transparent`}>{album.name}</h1>
          <p className="text-[13.5px] text-neutral-400 mt-2">{videos.length} {lang === 'sv' ? 'videor' : 'فيديو'} · {lang === 'sv' ? 'Video' : 'فيديو'} {idx + 1} {lang === 'sv' ? 'av' : 'من'} {videos.length}</p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          {/* Player */}
          <div>
            <div className="relative rounded-3xl overflow-hidden bg-black shadow-2xl border border-white/10 aspect-video">
              <video
                ref={videoRef}
                src={`${API}/files/${current.storage_path}`}
                poster={current.thumb_path ? `${API}/files/${current.thumb_path}` : undefined}
                autoPlay
                muted={muted}
                playsInline
                onEnded={next}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={prev} disabled={idx === 0} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center"><SkipBack className="w-4 h-4 rtl:rotate-180" /></button>
              <button onClick={togglePlay} className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform">
                {playing ? <Pause className="w-5 h-5 fill-black" /> : <Play className="w-5 h-5 fill-black ml-0.5" />}
              </button>
              <button onClick={next} disabled={idx === videos.length - 1} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center"><SkipForward className="w-4 h-4 rtl:rotate-180" /></button>
              <div className="flex-1" />
              <button onClick={() => setMuted(v => !v)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button onClick={shareAlbum} className={`h-11 px-4 rounded-full ${theme.btn} text-white font-bold text-[13px] flex items-center gap-2`}><Share2 className="w-3.5 h-3.5" /> {lang === 'sv' ? 'Dela' : 'مشاركة'}</button>
            </div>
            <div className="mt-4">
              <h2 className="text-xl font-black tracking-tight">{current.title}</h2>
              <p className="text-[12.5px] text-neutral-500 mt-1">{new Date(current.created_at).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')}</p>
            </div>
          </div>

          {/* Playlist */}
          <aside className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden max-h-[600px] flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <ListMusic className={`w-4 h-4 ${theme.chip}`} />
              <span className="text-[13.5px] font-bold">{lang === 'sv' ? 'Spellista' : 'قائمة التشغيل'}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {videos.map((v, i) => (
                <button key={v.id} onClick={() => setIdx(i)} className={`w-full flex items-center gap-3 p-2 rounded-xl transition-colors text-left rtl:text-right ${i === idx ? `${theme.ringBg} border ${theme.ring}` : 'hover:bg-white/10 border border-transparent'}`}>
                  <div className="relative w-20 h-12 rounded-md bg-neutral-800 overflow-hidden flex-shrink-0">
                    {v.thumb_path ? <img src={`${API}/files/${v.thumb_path}`} alt="" className="w-full h-full object-cover" /> : <div className={`w-full h-full bg-gradient-to-br ${theme.from} ${theme.to}`} />}
                    {i === idx && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Play className="w-4 h-4 fill-white text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[12.5px] font-bold truncate ${i === idx ? theme.accent : 'text-white'}`}>{v.title}</div>
                    <div className="text-[10.5px] text-neutral-500">{v.duration ? `${Math.round(v.duration)}s` : ''}</div>
                  </div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AlbumView;
